import { useMemo } from 'react';

import { collection, doc, DocumentReference } from 'firebase/firestore';

import { useDocumentData, useDocumentDataOnce } from 'react-firebase-hooks/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import League from '../types/League';

import { useFirestoreError } from './useFirestoreError';

interface UseLeagueProps {
  orgId?: string | null;
  leagueId?: string | null;
  once?: boolean;
}

export default function useLeague(props: UseLeagueProps) {
  const { orgId, leagueId, once = false } = props;
  const { isGod } = useAuth();
  const { userState, isOrgActive } = useData();

  const leagueRef = useMemo(() => {
    if (!orgId || !leagueId) return null;

    return doc(collection(firestore, 'org', orgId, 'league'), leagueId) as DocumentReference<League>;
  }, [orgId, leagueId]);

  // Both hooks must always be called (rules of hooks), but only one gets a non-null ref.
  // In "once" mode (edit), fetch once with no listener — avoids permission-denied on soft-delete.
  const [liveLeague, isLiveLoading, liveError] = useDocumentData<League>(once ? null : leagueRef);
  const [onceLeague, isOnceLoading, onceError] = useDocumentDataOnce<League>(once ? leagueRef : null);

  const league = once ? onceLeague : liveLeague;
  const isLeagueLoading = once ? isOnceLoading : isLiveLoading;
  const leagueError = once ? onceError : liveError;
  useFirestoreError('useLeague', leagueError);

  if (userState.status === 'logged_out') {
    return {
      league: null,
      isLeagueLoading: false,
      leagueError: new Error('User must be logged in to view leagues'),
    };
  }

  if (userState.status === 'ready' &&
    userState.userProfile &&
    !isLeagueLoading &&
    !leagueError &&
    !isGod &&
    orgId &&
    leagueId && !isOrgActive(orgId)) {
    return {
      league: null,
      isLeagueLoading: false,
      leagueError: new Error('Not authorized to view this league'),
    };
  }

  if (orgId === null && leagueId === null) {
    return {
      league: null,
      isLeagueLoading: false,
    };
  }

  return {
    league: league || null,
    isLeagueLoading: isLeagueLoading ||
      (userState.status === 'authenticating_user') ||
      (userState.status === 'authenticating_profile') ||
      (userState.status === 'authenticating_memberships'),
    leagueError,
  };
}
